import { motion } from "framer-motion";
import { Check } from "lucide-react";

const freePerks = [
  "Free forever — $0, no dues",
  "Access to every monthly drop",
  "Member pricing on all bottles",
  "Tasting notes with every wine",
  "Skip, swap or cancel anytime",
];

const premiumPerks = [
  "Everything in Free",
  "Priority allocation on every drop",
  "Free shipping, always",
  "Reserve & library bottle access",
  "Concierge swaps — love it or we replace it",
];

const Perk = ({ text, dark }) => (
  <li className={`flex items-start gap-3 text-sm ${dark ? "text-paper/85" : "text-ink/70"}`}>
    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-gold" : "text-wine"}`} />
    {text}
  </li>
);

export const Tiers = ({ onJoin, member, onUpgrade, upgrading }) => (
  <section id="membership" className="border-t border-ink/10 bg-[#F3EEE3] py-24 md:py-36" data-testid="tiers-section">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="font-serif text-4xl tracking-tight md:text-5xl"
      >
        Two ways <em className="text-wine">in</em>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="mt-6 max-w-md text-sm leading-relaxed text-ink/60"
      >
        Everyone starts free. Premium is there the moment you want more — and only then.
      </motion.p>

      <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col border border-ink/15 bg-paper p-10 md:p-14"
          data-testid="free-tier-card"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-ink/50">The club</span>
          <div className="mt-6 flex items-end gap-3">
            <span className="font-serif text-6xl tracking-tight">$0</span>
            <span className="pb-2 text-sm text-ink/50">forever</span>
          </div>
          <ul className="mt-10 space-y-4">
            {freePerks.map((p) => <Perk key={p} text={p} />)}
          </ul>
          <button
            data-testid="free-tier-join-button"
            onClick={onJoin}
            className="mt-12 rounded-full border border-ink/30 px-8 py-4 text-sm uppercase tracking-widest transition-colors duration-300 hover:border-wine hover:bg-wine hover:text-paper"
          >
            {member ? "You're in" : "Join free"}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex flex-col overflow-hidden bg-wine p-10 text-paper md:p-14"
          data-testid="premium-tier-card"
        >
          <img
            src="https://images.unsplash.com/photo-1592119748016-a61c40a44320?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-10 w-56 rotate-12 opacity-25"
            style={{ clipPath: "polygon(12% 0, 100% 8%, 88% 100%, 0 92%)" }}
          />
          <span className="text-xs uppercase tracking-[0.3em] text-gold">Premium</span>
          <div className="mt-6 flex items-end gap-3">
            <span className="font-serif text-6xl tracking-tight">$19</span>
            <span className="pb-2 text-sm text-paper/60">/ month · cancel anytime</span>
          </div>
          <ul className="mt-10 space-y-4">
            {premiumPerks.map((p) => <Perk key={p} text={p} dark />)}
          </ul>
          <button
            data-testid="premium-tier-upgrade-button"
            onClick={member?.tier === "premium" ? undefined : (member ? onUpgrade : onJoin)}
            disabled={upgrading || member?.tier === "premium"}
            className="relative z-10 mt-12 rounded-full bg-gold px-8 py-4 text-sm uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-paper disabled:cursor-default disabled:opacity-70"
          >
            {member?.tier === "premium"
              ? "You're Premium"
              : upgrading
                ? "Opening checkout…"
                : member
                  ? "Upgrade to Premium"
                  : "Join free first, upgrade anytime"}
          </button>
        </motion.div>
      </div>
    </div>
  </section>
);
